// Liveness chips for the two AI layers (see AiPresence). The Reflex chip shows
// the always-on hosted instant classifier; the Agent chip shows whether the
// user's deliberate Claude Code is actually connected to the round — the signal
// that's otherwise invisible (a paired session looks identical with or without a
// Claude Code attached, because the reflex answers regardless). Composes the
// Badge primitive like ConnectionChip; no new design tokens.

import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import type { AgentState, AiPresence, ReflexState } from '@/lib/ai/types'

type Variant = 'default' | 'secondary' | 'destructive' | 'outline'

interface ChipSpec {
  label: string
  variant: Variant
  /** Pulse the dot when the layer is live (hearing / listening / working). */
  live: boolean
  title: string
}

const REFLEX: Record<ReflexState, ChipSpec> = {
  connecting: {
    label: 'connecting…',
    variant: 'secondary',
    live: false,
    title: 'Reflex — the always-on hosted instant layer. Connecting to the bridge.'
  },
  connected: {
    label: 'connected',
    variant: 'default',
    live: true,
    title: 'Reflex connected — the hosted instant classifier is hearing the room.'
  },
  unavailable: {
    label: 'unavailable',
    variant: 'destructive',
    live: false,
    title:
      'Reflex unavailable — the bridge has no server-side API key, so the round can’t run.'
  }
}

const AGENT: Record<AgentState, ChipSpec> = {
  off: {
    label: 'not connected',
    variant: 'outline',
    live: false,
    title:
      'No Claude Code is connected — the room is running on the reflex alone. Run /brainstorm to attach one.'
  },
  connected: {
    label: 'connected',
    variant: 'default',
    live: true,
    title: 'Claude Code connected — parked on WaitForDelegation, listening for work.'
  },
  working: {
    label: 'working…',
    variant: 'secondary',
    live: true,
    title: 'Claude Code is handling a delegation.'
  }
}

function Chip({ name, spec }: { name: string; spec: ChipSpec }) {
  return (
    <Badge variant={spec.variant} title={spec.title} className="gap-1.5">
      <span
        className={cn('size-1.5 rounded-full bg-current', spec.live && 'animate-pulse')}
      />
      <span className="font-medium">{name}</span>
      <span className="opacity-70">{spec.label}</span>
    </Badge>
  )
}

export function AiPresenceChips({ presence }: { presence: AiPresence }) {
  return (
    <>
      <Chip name="Reflex" spec={REFLEX[presence.reflex]} />
      <Chip name="Agent" spec={AGENT[presence.agent]} />
    </>
  )
}
